import React, { useMemo } from 'react';
import { t } from '@bay-email/core';
import { SYSTEM_FONT_OPTIONS } from '@extensions/constants';

type FontGroupKey = 'system' | 'google';

export interface FontOption {
	value: string;
	label: React.ReactNode;
	plainLabel: string;
	group: FontGroupKey;
	groupLabel: string;
}

export interface FontOptionGroup {
	key: FontGroupKey;
	label: string;
	options: FontOption[];
}

export function useFontFamily() {
	const { fontList, fontGroups } = useMemo(() => {
		const systemFonts: FontOption[] = [];
		const seenValues = new Set<string>();
		const systemGroupLabel = t('System fonts');

		const addOption = (value: string, plainLabel: string, group: FontGroupKey) => {
			if (!value || seenValues.has(value)) {
				return;
			}

			seenValues.add(value);
			const option: FontOption = {
				value,
				plainLabel,
				group: 'system',
				groupLabel: systemGroupLabel,
				label: <span style={{ fontFamily: value }}>{plainLabel}</span>,
			};

			systemFonts.push(option);
		};

		SYSTEM_FONT_OPTIONS.forEach(item => {
			addOption(item.value, item.label, 'system');
		});

		const byLabel = (a: FontOption, b: FontOption) =>
			a.plainLabel.localeCompare(b.plainLabel, undefined, {
				sensitivity: 'base',
			});

		const systemByLabel = (a: FontOption, b: FontOption) => {
			const aIsSystemDefault = a.plainLabel.toLowerCase() === 'system font';
			const bIsSystemDefault = b.plainLabel.toLowerCase() === 'system font';

			if (aIsSystemDefault && !bIsSystemDefault) {
				return -1;
			}

			if (!aIsSystemDefault && bIsSystemDefault) {
				return 1;
			}

			return byLabel(a, b);
		};

		systemFonts.sort(systemByLabel);

		const groupedFonts: FontOptionGroup[] = [
			{
				key: 'system',
				label: systemGroupLabel,
				options: systemFonts,
			},
		];

		return {
			fontList: [...systemFonts],
			fontGroups: groupedFonts,
		};
	}, []);

	return {
		fontList,
		fontGroups,
	};
}
